import { ContentTitle, SectionTitle, Subtitle } from '../../../components/typography/Heading';
import { useMemo, useRef, useState } from 'react';
import { useSubproject } from '../../../components/hooks/useSubproject';
import Main from '../../../frame/main/Main';
import LoaderBar from '../../../components/loader-bar/LoaderBar';
import { useSelector } from 'react-redux';
import Image from '../../../components/image/Image';
import RewardList from '../../reward/RewardList';
import { getPublishedRewardsForProject } from '../../projects-reducer';
import ProjectLatestActivity from '../../project-latest-activity/ProjectLatestActivity';
import GenericHelmet from '../../generic-helmet/GenericHelmet';
import ProjectContext from '../../project-context';
import ThemeContext from '../../../context/theme-context';
import { getSubprojectTitle } from '../../subproject-model';
import ProjectSupportButtons from '../project-support-buttons/ProjectSupportButtons';
import RoundedButtonLinkAction from '../../../components/button/rounded-button-link-action/RoundedButtonLinkAction';
import RoundedButtonSecondary from '../../../components/button/rounded-button-secondary/RoundedButtonSecondary';
import ThemeContainer from '../../../components/theme-container/ThemeContainer';
import { isFinished, isProjectADugnad } from '../../project-model';
import { FloatingTotal } from '../../payment/cart/FloatingTotal';
import QRCode from 'qrcode.react';
import RoundedButtonAction from '../../../components/button/rounded-button-action/RoundedButtonAction';
import { SubprojectOwnerHeader } from './SubprojectOwnerHeader';
import { SubprojectInvitedHeader } from './SubprojectInvitedHeader';
import { Link } from 'react-router';
import TrackEventLink from '../../../components/track-event-link/TrackEventLink';
import { useCart } from '../../project-hooks/useCart';
import styles from './SubprojectShow.module.less';
import { formatMoneyKr } from '../../../utils';

const SubprojectShow = ({ project, defaultShareImage, projectTheme }) => {
    const { items: cartItems } = useCart();
    const baseURL = useSelector((state) => state.baseURL);
    const { subproject, isFetchingSubproject } = useSubproject();

    const publishedRewards = useSelector((state) => getPublishedRewardsForProject(state, project.id));
    const baseUrl = useSelector((state) => state.baseURL);
    const [showQr, setShowQr] = useState(false);

    const isDugnad = isProjectADugnad(project);
    const isPayToRegister = project?.profile?.payToRegisterSubproject;
    const isShowCollectedForSubprojects = project?.profile?.showCollectedForSubprojects;
    const hideSupportButton = project?.profile?.hideSupportButton;
    const title = useMemo(
        () => getSubprojectTitle({ subproject, isDugnad }),
        [subproject?.anonymous, subproject?.name, isDugnad]
    );
    const [showShareModal, setShowShareModal] = useState(false);

    const shareImageUrl = useMemo(() => {
        if (project?.img_url && project?.id && subproject?.id && subproject?.img_url) {
            const imageVersion = subproject?.image_version;
            const versionTag = imageVersion ? `?v=${imageVersion}` : '';

            return `${baseUrl}/api/public/project/${project.id}/subproject/${subproject.id}/shareImage.jpeg${versionTag}`;
        } else {
            return defaultShareImage;
        }
    }, [subproject?.id, project.id, project.img_url, baseUrl, defaultShareImage]);

    const isFetchingCards = useSelector((state) => state.projects.isFetchingCards) && !project.created_at;
    const rewardsRef = useRef(null);

    if (isFetchingCards || isFetchingSubproject || !subproject?.id) {
        return (
            <Main>
                <LoaderBar />
            </Main>
        );
    }

    const subprojectCollected = subproject.collected || subproject.sold_in_kr || 0;
    return (
        <ThemeContext.Provider value={{ theme: projectTheme }}>
            <Main>
                <GenericHelmet
                    title={title}
                    imageUrl={shareImageUrl}
                    imageHeight={624}
                    imageWidth={1200}
                    description={project.profile?.subProjectPurpose || project.purpose}
                    projectId={project.id}
                    subprojectId={subproject.id}
                    receiverName={project.receiver?.name}
                    path={`/project/${project.id}?subproject=${subproject.id}`}
                    noIndex={project.profile?.noIndex}
                />

                <ProjectContext.Provider value={project}>
                    {showQr && (
                        <div className={styles.qrContainer}>
                            <Image
                                className={styles.closeButton}
                                src="close.svg"
                                alt="Lukk"
                                role="button"
                                onClick={() => setShowQr(false)}
                            />
                            <ContentTitle className={styles.title}>{title}</ContentTitle>
                            <Image
                                src={subproject.img_url || subproject.fallback_img_url}
                                className={styles.profileImage}
                                alt={'Profilbilde for deltaker'}
                            />
                            <div className={styles.qrCode}>
                                <QRCode
                                    size={256}
                                    style={{
                                        height: 'auto',
                                        width: '100%',
                                    }}
                                    value={`${baseURL}/dugnad/${subproject.id}`}
                                    viewBox={`0 0 256 256`}
                                />
                            </div>
                            <p className={styles.url}>
                                Gå til spleis.no/{isDugnad ? 'dugnad' : 'delta'}/{subproject.id}
                            </p>
                            <div>
                                <RoundedButtonAction onClick={() => setShowQr(false)}>Lukk</RoundedButtonAction>
                            </div>
                        </div>
                    )}
                    <SubprojectOwnerHeader
                        showShare={() => setShowShareModal(true)}
                        showQr={() => {
                            window.scrollTo(0, 0);
                            setShowQr(true);
                        }}
                    />
                    <SubprojectInvitedHeader
                        showShare={() => setShowShareModal(true)}
                        showQr={() => {
                            window.scrollTo(0, 0);
                            setShowQr(true);
                        }}
                    />
                    <ThemeContainer className={styles.subprojectContainer}>
                        <Image
                            src={subproject.img_url || subproject.fallback_img_url}
                            className={styles.profileImage}
                            alt={'Profilbilde for deltaker'}
                        />
                        <div className={styles.contentWrapper}>
                            <ContentTitle className={styles.title}>{title}</ContentTitle>
                            {isShowCollectedForSubprojects && (
                                <SectionTitle> 🔥 {formatMoneyKr(subprojectCollected || 0)} innsamlet 🔥</SectionTitle>
                            )}
                            <Subtitle className={styles.title}>{project.title}</Subtitle>
                            <p className={styles.abstract}>{project.profile?.subProjectPurpose || project.purpose}</p>
                            <p>
                                <TrackEventLink
                                    to={`/project/${project.id}`}
                                    category={'subproject-goto-project'}
                                    action={'click'}
                                    properties={{
                                        'subproject-id': subproject.id,
                                        'project-id': project.id,
                                        isDugnad,
                                    }}
                                >
                                    {isDugnad
                                        ? 'Mer om dugnaden'
                                        : isPayToRegister
                                        ? 'Les mer om spleisen og meld deg på'
                                        : 'Mer om spleisen'}
                                </TrackEventLink>
                            </p>
                            {isFinished(project) ? (
                                <div className={styles.finishedBanner}>Avsluttet</div>
                            ) : (
                                <div className={styles.supportButtonContainer}>
                                    {!hideSupportButton && (
                                        <RoundedButtonLinkAction
                                            to={{
                                                pathname: `/project/${project.id}/payment`,
                                                search: new URLSearchParams({
                                                    subproject: subproject.id,
                                                }).toString(),
                                            }}
                                            category={
                                                cartItems?.length > 0
                                                    ? 'shoppingcart-checkout'
                                                    : 'subproject-support-no-reward'
                                            }
                                            action={'click'}
                                            properties={{
                                                'subproject-id': subproject.id,
                                                'project-id': project.id,
                                                buttonType: 'suproject-main',
                                            }}
                                        >
                                            {cartItems?.length > 0 ? 'Gå til kassen' : 'Støtt med valgfri sum'}
                                        </RoundedButtonLinkAction>
                                    )}
                                    <RoundedButtonSecondary
                                        onClick={() => setShowShareModal(true)}
                                        className={styles.shareButton}
                                    >
                                        Del {isDugnad && 'salgs'}siden
                                    </RoundedButtonSecondary>
                                    <RoundedButtonSecondary
                                        trackingCategory={'subproject-admin-qr'}
                                        trackingAction={'click'}
                                        trackingParameters={{
                                            'subproject-id': subproject.id,
                                            'project-id': project.id,
                                            everyone: true,
                                        }}
                                        onClick={() => {
                                            window.scrollTo(0, 0);
                                            setShowQr(true);
                                        }}
                                    >
                                        <i className={'fa-regular fa-qrcode'} /> Vis QR-kode
                                    </RoundedButtonSecondary>
                                </div>
                            )}
                            <div ref={rewardsRef}>
                                <RewardList
                                    rewards={publishedRewards}
                                    showSupportButton={false}
                                    showTitle={true}
                                    onProjectPage={false}
                                    titleClass={styles.productTitle}
                                    descriptionClass={styles.productDescription}
                                    className={styles.rewards}
                                    showCreateParticipantRewards={false}
                                />
                            </div>
                            {!isPayToRegister && (
                                <div className={styles.information}>
                                    {isDugnad && (
                                        <>
                                            <div>Denne siden er en del av en dugnad.</div>
                                            <Link to={`/project/${project.id}`}>Mer informasjon om dugnaden</Link>
                                        </>
                                    )}
                                    {!isDugnad && (
                                        <>
                                            <div>Denne siden er en del av en spleis hvor alle kan bli med.</div>
                                            <br />
                                            <Link to={`/project/${project.id}`}>Bli med du også!</Link>
                                        </>
                                    )}
                                </div>
                            )}
                            <ProjectLatestActivity
                                className={styles.latestActivity}
                                title={`Siste aktivitet for hele ${isDugnad ? 'dugnaden' : 'spleisen'}`}
                                linkToHistory={false}
                                limit={3}
                            />
                        </div>
                        <ProjectSupportButtons
                            className={styles.supportButtons}
                            showModal={showShareModal}
                            onCloseShareModal={() => setShowShareModal(false)}
                        />
                        <FloatingTotal />
                    </ThemeContainer>
                </ProjectContext.Provider>
            </Main>
        </ThemeContext.Provider>
    );
};
export default SubprojectShow;
